import React, { ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Form } from "antd";
import { FormInstance } from "antd/es/form";
import { useLocation } from "react-router";

interface FormDirtyState {
  dirty: boolean;
  markDirty: () => void;
  resetDirty: () => void;
}

const FormDirtyContext = React.createContext<FormDirtyState>({
  dirty: false,
  markDirty: () => undefined,
  resetDirty: () => undefined,
});

/**
 * Tracks whether anything in the page has been edited, so the Save button can stay
 * disabled until there is something to save. The edit buttons live outside the Form
 * (they submit through the form="form" attribute), so the state is shared by context.
 */
export default function useFormDirty() {
  return useContext(FormDirtyContext);
}

/**
 * Counts the values a page writes itself - `setFieldsValue` never reaches `onFormChange`.
 * Not for forms that hydrate through `setFieldsValue`, those would come out dirty on load.
 */
export function useDirtyTrackedForm<T>(form: FormInstance<T>): FormInstance<T> {
  const { markDirty } = useFormDirty();

  return useMemo<FormInstance<T>>(
    () => ({
      ...form,
      setFieldsValue: (values) => {
        markDirty();
        form.setFieldsValue(values);
      },
      setFields: (fields) => {
        markDirty();
        form.setFields(fields);
      },
      setFieldValue: (name, value) => {
        markDirty();
        form.setFieldValue(name, value);
      },
    }),
    [form, markDirty]
  );
}

export function FormDirtyProvider(props: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [state, setState] = useState({ dirty: false, pathname });
  if (state.pathname !== pathname) {
    setState({ dirty: false, pathname });
  }
  const dirty = state.pathname === pathname && state.dirty;

  const markDirty = useCallback(() => setState({ dirty: true, pathname }), [pathname]);
  const resetDirty = useCallback(() => setState({ dirty: false, pathname }), [pathname]);
  const value = useMemo(() => ({ dirty, markDirty, resetDirty }), [dirty, markDirty, resetDirty]);

  return (
    <FormDirtyContext.Provider value={value}>
      {/* onFormChange fires for every nested Form, named or not */}
      <Form.Provider onFormChange={markDirty}>{props.children}</Form.Provider>
    </FormDirtyContext.Provider>
  );
}
